import { forwardRef } from 'react';

import { Avatar as BaseAvatar } from '@base-ui/react/avatar';
import { HandIcon, UserIcon, UsersIcon } from 'components/Icon';
import { cn } from 'components/utils';

import { avatarDotRecipe, avatarHandRecipe, avatarInitialsRecipe, avatarRecipe } from './Avatar.recipe';
import { getInitials } from './getInitials';

import type { AvatarProps, AvatarSize } from './types';

/** The fallback glyph, at roughly half the box and always on a 4px step. */
const ICON_SIZE: Record<AvatarSize, number> = { 20: 12, 24: 12, 32: 16, 40: 20, 56: 28, 64: 32, 72: 36, fill: 32 };

/** The hand glyph inside the hand-raised badge, 70% of the badge. */
const HAND_ICON_SIZE: Record<AvatarSize, number> = { 20: 10, 24: 10, 32: 10, 40: 12, 56: 14, 64: 14, 72: 14, fill: 14 };

const STATUS_LABEL = { online: 'Online', 'hand-raised': 'Hand raised' } as const;

/**
 * Design System 2.0 avatar.
 *
 * Built from the Figma set (file 5aSkE0U4TeWbuIZDvccHjc, node 1872:19488); the
 * decision record is "Brand active" (2026-09-24). Replaces, over time, the legacy `UserAvatar`,
 * `MemberAvatar`'s picture, `EventUserAvatar`, `SpeakerAvatar`'s circle, `CommunityLogo` and the
 * raw MUI `Avatar` calls — none of which it touches yet.
 *
 * The picture of a person, or the mark of an organisation. Content is derived, never chosen:
 *
 *   photo loads         → the photo, with a neutral rim so a pale photo keeps an edge
 *   photo loading       → a quiet neutral disc and NO initials, so there is no initials-then-photo
 *                         flash
 *   no photo, or broken → initials on the brand pair; one letter at 24px and below
 *   no name either      → the person (or group) icon
 *
 * Base UI's Avatar owns the image loading state; this wraps it the way Switch and Checkbox wrap
 * theirs, and reads the loading status through the `className` function so the photo-only slot
 * values apply exactly while a photo is (or is about to be) showing.
 *
 * Display-only. Never a button or a link on its own: put it inside the row, card or tile that is.
 * Status is a person thing, so `status` and `speaking` do not exist on `type="organisation"`.
 * Host, Owner and Admin are a `Tag` beside the name, never a badge on the avatar.
 *
 * Theme is not a prop. Colours resolve from the nearest `.dark` ancestor through the token layer,
 * so the same markup is correct on the light organiser pages and the dark live room.
 */
export const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(
  (
    {
      className,
      decorative = false,
      name,
      size = 32,
      speaking,
      src,
      status,
      statusLabel,
      type = 'person',
      unknownLabel = 'Unknown',
      ...rest
    },
    ref
  ) => {
    const fill = size === 'fill';
    const initials = getInitials(name, !fill && size <= 24 ? 1 : 2);
    const FallbackIcon = type === 'organisation' ? UsersIcon : UserIcon;
    const statusText = status ? (statusLabel ?? STATUS_LABEL[status]) : undefined;
    const label = [name?.trim() || unknownLabel, statusText].filter(Boolean).join(', ');

    return (
      <BaseAvatar.Root
        ref={ref}
        {...rest}
        // Standing alone, the avatar is ONE image named "Zoë O'Brien, Online" — its parts are
        // presentational under `role="img"`, so the status has to be in that name. Decorative
        // (the name is printed beside it) the picture is silent, but a status is still news the
        // printed name does not carry, so the dot or badge stays exposed on its own.
        aria-label={decorative ? undefined : label}
        className={({ imageLoadingStatus }) =>
          cn(
            avatarRecipe({ size, type }),
            // The photo slot values while a photo is showing OR still loading. Once it errors the
            // brand pair comes back with the initials.
            !!src && imageLoadingStatus !== 'error' && 'ds-avatar-photo',
            className
          )
        }
        data-speaking={speaking || undefined}
        role={decorative ? undefined : 'img'}
      >
        {!!src && (
          <BaseAvatar.Image
            alt=""
            className="absolute inset-0 size-full rounded-[inherit] object-cover"
            decoding="async"
            height={fill ? undefined : size}
            src={src}
            width={fill ? undefined : size}
          />
        )}
        <BaseAvatar.Fallback
          aria-hidden
          className={({ imageLoadingStatus }) =>
            cn('inline-flex items-center justify-center', imageLoadingStatus === 'loading' && 'invisible')
          }
        >
          {initials ? (
            <span className={avatarInitialsRecipe({ fill })}>{initials}</span>
          ) : (
            <FallbackIcon className={fill ? 'size-[50cqh]' : undefined} size={ICON_SIZE[size]} />
          )}
        </BaseAvatar.Fallback>
        {status === 'online' && (
          <span
            aria-hidden={!decorative || undefined}
            aria-label={decorative ? statusText : undefined}
            className={avatarDotRecipe({ size })}
            role={decorative ? 'img' : undefined}
          />
        )}
        {status === 'hand-raised' && (
          <span
            aria-hidden={!decorative || undefined}
            aria-label={decorative ? statusText : undefined}
            className={avatarHandRecipe({ size })}
            role={decorative ? 'img' : undefined}
          >
            <HandIcon size={HAND_ICON_SIZE[size]} />
          </span>
        )}
      </BaseAvatar.Root>
    );
  }
);
